import type { DeployMode, DeploySignal, DeploySignalStatus, Deployment } from "../../../types/strategyDeploy";

export const MODE_LABEL: Record<DeployMode, string> = {
  paper: "Paper",
  approve: "Copilot",
  auto: "Autopilot",
};

export const MODE_BLURB: Record<DeployMode, string> = {
  paper: "Trades every signal on paper with live prices. Nothing is sent to a broker.",
  approve: "You approve each signal, here or with the Telegram buttons, then the order goes to your broker.",
  auto: "Signals go straight to your broker, inside the limits you set.",
};

export const BROKER_LABEL: Record<string, string> = {
  liquidbrokers: "Liquid Charts",
  ctrader: "cTrader",
};

/** Broker account codes as people read them (Liquid codes carry a `default:` domain prefix). */
export function accountLabel(code: string | null | undefined): string {
  return String(code ?? "").replace(/^[a-z]+:/i, "") || "N/A";
}

export const STATUS_LABEL: Record<string, string> = {
  running: "Live",
  paused: "Paused",
  stopped: "Stopped",
  killed: "Killed",
  error: "Error",
};

export const SIGNAL_STATUS_LABEL: Record<DeploySignalStatus, string> = {
  pending: "Waiting for you",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  submitted: "Sending",
  open: "Open",
  closed: "Closed",
  failed: "Not sent",
  skipped: "Skipped",
  paper: "Paper · open",
};

/** Tessera palette: surf = live / neutral, green = profit, red = loss / danger. */
export const SURF = "var(--brand-tropical-surf)";
export const GREEN = "var(--tessera-pnl-positive)";
export const RED = "var(--brand-glacial-red)";

export function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "N/A";
  const abs = Math.abs(v);
  const digits = abs >= 1000 ? 2 : abs >= 10 ? 3 : 5;
  return v.toLocaleString(undefined, { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits });
}

export function fmtMoney(v: number | null | undefined, signed = true): string {
  if (v == null || !Number.isFinite(v)) return "N/A";
  const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!signed) return `$${s}`;
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}$${s}`;
}

export function fmtR(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "N/A";
  return `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(2)}R`;
}

export function fmtPct(v: number | null | undefined, digits = 0): string {
  if (v == null || !Number.isFinite(v)) return "N/A";
  return `${(v * (Math.abs(v) <= 1 ? 100 : 1)).toFixed(digits)}%`;
}

export function signColor(v: number | null | undefined): string | undefined {
  if (v == null || !Number.isFinite(v) || v === 0) return undefined;
  return v > 0 ? GREEN : RED;
}

export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "N/A";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "N/A";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function fmtClock(iso: string | null | undefined): string {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "N/A";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function countdown(iso: string | null | undefined, now = Date.now()): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - now;
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "0:00";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function rewardRisk(sig: Pick<DeploySignal, "entry_price" | "stop_price" | "target_price">): number | null {
  const { entry_price: e, stop_price: s, target_price: t } = sig;
  if (e == null || s == null || t == null) return null;
  const risk = Math.abs(e - s);
  return risk > 0 ? Math.abs(t - e) / risk : null;
}

export function isLive(d: Deployment): boolean {
  return d.status === "running";
}

/** Closed trades oldest to newest with the running R / money (for the live curve). */
export function cumulative(signals: DeploySignal[]): { t: string; r: number; pnl: number }[] {
  const closed = signals
    .filter((s) => s.status === "closed" && s.exit_time)
    .sort((a, b) => String(a.exit_time).localeCompare(String(b.exit_time)));
  let r = 0;
  let pnl = 0;
  return closed.map((s) => {
    r += s.r_multiple ?? 0;
    pnl += s.pnl ?? 0;
    return { t: String(s.exit_time), r: Number(r.toFixed(3)), pnl: Number(pnl.toFixed(2)) };
  });
}
